<template>
  <a-alert
    v-if="errorMessage"
    :message="errorMessage"
    type="error"
    show-icon
    closable
    @close="errorMessage = ''"
    class="mb-2"
  >
    <template #description>
      <span>{{ t("app.fetchDataFailed") }}</span>
      <a-button type="link" size="small" @click="fetchData">{{ t("common.retry") }}</a-button>
    </template>
  </a-alert>
  <LayoutPage />
  <UserCenter />
  <Support />
  <!-- <UpgradeWall /> -->
</template>
<script setup lang="ts">
import LayoutPage from "@/layouts/Index.vue";
import { set } from "lodash-es";
import retry from "async-retry";
import { generateID } from "server/utils/tool";
import { globalEventHandler } from "./services/globalEventHandler";
import { loadLocaleNamespace } from "./locales/loader";
import { preloadCommonLocales } from "./locales/preload";
import { initSession } from "./utils";

const userStore = useUserStore();
const { setUser } = userStore;
const { setPlan } = usePlanStore();
const { setConfig } = useReportConfigStore();
const { t } = useI18n();
const { fetchAdmin } = useAdminStore();

const errorMessage = ref<string>("");
const router = useRouter();

const route = useRoute();

const currentName = computed(() => {
  if (router.currentRoute.value.meta?.i18Alias) {
    return router.currentRoute.value.meta.i18Alias as string;
  }
  return router.currentRoute.value.name as string;
});

// 监听路由变化，确保页面切换时滚动到顶部
watch(
  () => route.path,
  (newPath, oldPath) => {
    const last = newPath.split("/").pop() || "";
    if (oldPath.endsWith(last)) return;
    nextTick(() => {
      const contentElement = document.querySelector(".overflow-y-auto");
      if (contentElement) {
        contentElement.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  }
);

/**
 * 重新加载当前路由的语言包（用于语言切换时）
 */
async function reloadCurrentLocale(lang: string) {
  if (currentName.value) {
    await loadLocaleNamespace(lang, currentName.value);
  }
  // 同时确保加载common语言包
  preloadCommonLocales();
}

watch(() => systemStorage.value.lang, reloadCurrentLocale);

// 创建 Broadcast Channel 用于跨标签页通信
let broadcastChannel;

// 当前实例的唯一标识符
const instanceId = generateID();
// sessionStorage.setItem("instanceId", instanceId);

// 初始化 Broadcast Channel（处理兼容性问题）
function initBroadcastChannel() {
  if (typeof BroadcastChannel !== "undefined") {
    broadcastChannel = new BroadcastChannel("app_state_sync");

    // 监听其他标签页发送的消息
    broadcastChannel.addEventListener("message", async event => {
      const { type, payload, senderId } = event.data;

      // 忽略自己发送的消息
      if (senderId === instanceId) return;

      switch (type) {
        case "PASSWORD_CHANGED":
          // 更新本地存储的 token
          if (payload.token) {
            authStorage.value.lastLoginType = payload.lastLoginType;
          }
          notification.info({
            message: t("app.password_changed_other_tab"),
            duration: null,
          });
          break;
        case "USER_PROFILE_UPDATED":
          // 更新用户信息
          if (payload.user) {
            setUser(payload.user);
            notification.info({
              message: t("app.profile_updated_other_tab"),
              duration: null,
            });
          }
          break;
        case "REPORT_CONFIG_UPDATED":
          // 更新报告配置
          if (payload.config) {
            setConfig(payload.config);
            notification.info({
              message: t("app.report_updated_other_tab"),
              duration: null,
            });
          }
          break;
        case "USER_LOGGED_OUT":
          await logout();
          set(authStorage.value, "loginState.redirectUrl", router.currentRoute.value.path);
          notification.info({
            message: t("app.logged_out_other_tab"),
            duration: null,
          });
          break;
      }
    });
  }
}

// 在组件卸载时关闭 Broadcast Channel
function closeBroadcastChannel() {
  if (broadcastChannel) {
    broadcastChannel.close();
  }
}
async function fetchData() {
  errorMessage.value = "";
  try {
    const { user, plan, config } = await retry(
      async () => {
        const response = await get("user/info");
        if (!response.user || !response.plan) throw new Error("Invalid response");
        return response;
      },
      {
        retries: 3,
        factor: 2,
        minTimeout: 2000,
        maxTimeout: 5000,
        onRetry: (err: any, attempt) => {
          // console.warn(`Retry attempt ${attempt} for user/info: ${err.message}`);
        },
      }
    );
    setUser(user);
    setPlan(plan);
    setConfig(config);
    globalEventHandler.init(userStore.user.user_id);
  } catch (error: any) {
    errorMessage.value = t("app.fetchDataFailed");
    message.error(errorMessage.value);
    setUser();
    setPlan();
    setConfig();
  }
}

// emitter.on("user-logged-in", fetchData); // 监听登录事件
// emitter.on("admin-logged-in", () => fetchAdmin());
// emitter.on("user-logout", () => {
//   setUser();
//   setPlan();
//   setConfig();
// });

// 页面加载完成后预加载常用语言包
onMounted(() => {
  // 初始化 Broadcast Channel
  // initBroadcastChannel();
  preloadCommonLocales();
  initSession();
});

// onUnmounted(closeBroadcastChannel);
</script>
